import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building, Brain } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { useRBAC } from "../hooks/useRBAC";
import Navigation from "../components/Navigation";
import Card from "../components/Card";
import LoadingSpinner from "../components/LoadingSpinner";
import { CheckCircle, AlertTriangle, Shield, X, Search, Edit } from "lucide-react";
import ReactSpeedometer from "react-d3-speedometer";
import { RiskScoreFootnote, PredictiveRiskBars } from "./RiskScoreCards";
import Button from "../components/Buttons";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import IntelligenceNarrative from "../components/IntelligenceNarrative";
import { severityPostureScore, severityPostureLevel } from '../utils/riskScoringEngine';
import { getComplianceColor, getComplianceBarColor, RISK_GAUGE_SEGMENT_STOPS, RISK_GAUGE_SEGMENT_COLORS } from '../utils/riskColors';
import { useLiveDashboard } from '../hooks/useLiveDashboard';

const org = JSON.parse(localStorage.getItem("selectedOrganization"));
// User Management Component
const Dashboard = () => {
    const [analytics, setAnalytics] = useState(null);

    const apiCall = useApi();
    const { user, logout } = useAuth();
    const { canManageOrganizationRisks } = useRBAC();

    const [organizationAnalytics, setOrganizationAnalytics] = useState({});
    const [appsWithRisks, setAppsWithRisks] = useState([]);

    const [loading, setLoading] = useState(true);
    const [latestAssessment, setLatestAssessment] = useState(null);
    const [complianceData, setComplianceData] = useState({
        compliance_status: {},
        overall_compliance: 0,
    });
    const [loadingCompliance, setLoadingCompliance] = useState(true);
    const [zeroTrustScores, setZeroTrustScores] = useState({});
    const [zeroTrustOverall, setZeroTrustOverall] = useState(0);
    const [loadingZeroTrust, setLoadingZeroTrust] = useState(false);

    const [predictiveRisk, setPredictiveRisk] = useState({
        current: 0,
        day45: 0,
        day90: 0,
        day180: 0,
        escalating180: 0,
    });
    const [loadingPredictive, setLoadingPredictive] = useState(false);

    // Raw (unscaled) values — scaled by remediation ratio in derived useEffect
    const [rawCompliance, setRawCompliance] = useState({ risk_status: {}, overall_risk: 0 });
    const [rawPredictive, setRawPredictive] = useState({ current: 0, day45: 0, day90: 0, day180: 0, escalating180: 0 });

    const [riskCategories, setRiskCategories] = useState({});

    useEffect(() => {
        const org = JSON.parse(localStorage.getItem("selectedOrganization"));
    }, []);

    // Load all dashboard data. `silent` skips the loading-state toggles so the live background
    // refresh swaps numbers in place with no spinner/flicker; the mount call passes silent=false for
    // the normal first-load spinners. The independent fetches run in parallel.
    const refreshData = useCallback(async (silent = false) => {
        const org = JSON.parse(localStorage.getItem("selectedOrganization"));
        if (!org?.organizationId) return;

        const fetchOrgAnalytics = async () => {
            if (!silent) setLoading(true);
            try {
                // 1️⃣ Overall risk score
                const risks = await apiCall('GET', `/aiRisks/details`);
                const arr = Array.isArray(risks) ? risks : [];
                // Exclude remediated risks — score reflects only open/active risks
                const activeRisks = arr.filter(r => !r.isRemediationCompleted);
                const remediatedCount = arr.length - activeRisks.length;
                const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
                activeRisks.forEach(r => {
                    const sev = (r.riskSeverity || '').toUpperCase();
                    if (counts.hasOwnProperty(sev)) counts[sev]++;
                });
                const score = severityPostureScore(counts);
                const level = severityPostureLevel(score);

                // 2️⃣ Vulnerability distribution
                const dist = await apiCall('GET', `/aiRisks/vulnerability-distribution`);

                setOrganizationAnalytics({
                    overall_risk_score: score,
                    overall_RiskLevel: level,
                    total_risks: arr.length,            // all risks (including remediated)
                    active_risks: activeRisks.length,   // only active (non-remediated) risks
                    risk_distribution: counts,          // distribution of active risks only
                    remediated_risks: remediatedCount
                });

            } catch (err) {
                setOrganizationAnalytics({ overall_risk_score: 0, overall_RiskLevel: 'LOW', total_risks: 0, risk_distribution: {} });
            } finally {
                if (!silent) setLoading(false);
            }
        };

        const fetchLatestAssessment = async () => {
            try {
                const assessments = await apiCall(
                    "GET",
                    `/aiAssessments?includeAll=true`
                );

                if (assessments && assessments.length > 0) {
                    const latest = assessments.sort(
                        (a, b) => new Date(b.aiAssessmentDate) - new Date(a.aiAssessmentDate)
                    )[0];
                    setLatestAssessment(latest);
                }
            } catch (err) {}
        };

        const fetchCompliance = async () => {
            if (!silent) setLoadingCompliance(true);
            try {
                const data = await apiCall(
                    "GET",
                    `/aiRisks/compliance-status?useQuestionCategory=true`
                );
                setRawCompliance({
                    risk_status: data?.risk_status || {},
                    overall_risk: data?.overall_risk || 0,
                });
            } catch (err) {
                setRawCompliance({ risk_status: {}, overall_risk: 0 });
            } finally {
                if (!silent) setLoadingCompliance(false);
            }
        };

        const fetchPredictiveRisk = async () => {
            if (!silent) setLoadingPredictive(true);
            try {
                const data = await apiCall("GET", `/aiRisks/organization/predictive-risk`);
                setRawPredictive({
                    current: data?.currentScore || 0,
                    day45: data?.predictive45DayScore || 0,
                    day90: data?.predictive90DayScore || 0,
                    day180: data?.predictive180DayScore || 0,
                    escalating180: data?.escalating180Count || 0,
                });
            } catch (err) {
                setRawPredictive({ current: 0, day45: 0, day90: 0, day180: 0, escalating180: 0 });
            } finally {
                if (!silent) setLoadingPredictive(false);
            }
        };

        const fetchRiskCategories = async () => {
            try {
                const data = await apiCall(
                    "GET",
                    `/aiRisks/compliance-status`
                );
                setRiskCategories(data?.risk_status || {});
            } catch (err) {
                setRiskCategories({});
            }
        };

        await Promise.all([
            fetchOrgAnalytics(),
            fetchLatestAssessment(),
            fetchCompliance(),
            fetchPredictiveRisk(),
            fetchRiskCategories(),
        ]);
    }, [apiCall]);

    // First load (with spinners), then keep the dashboard live: the change-token poll re-runs the
    // fetch silently whenever risks/remediations change, with no visible loading state.
    useEffect(() => { refreshData(false); }, [refreshData]);
    const liveRefresh = useCallback(() => refreshData(true), [refreshData]);
    useLiveDashboard({ refresh: liveRefresh });

    useEffect(() => {
        if (!latestAssessment?.aiAssessmentID) return;

        const fetchZeroTrust = async () => {
            setLoadingZeroTrust(true);
            try {
                const data = await apiCall(
                    "GET",
                    `/aiRisks/aiAssessment/${latestAssessment.aiAssessmentID}/zero-trust-scores`
                );

                // ✅ Save in separate state
                setZeroTrustScores(data?.scores || {});
                setZeroTrustOverall(data?.overallScore || 0);

            } catch (err) {
                setZeroTrustScores({});
                setZeroTrustOverall(0);
            } finally {
                setLoadingZeroTrust(false);
            }
        };

        fetchZeroTrust();
    }, [latestAssessment]);

    // Derived: pass through risk-table-based data (already excludes remediated risks)
    useEffect(() => {
        setComplianceData({
            compliance_status: Object.fromEntries(
                Object.entries(rawCompliance.risk_status || {}).map(([k, v]) => [k, v || 0])
            ),
            overall_compliance: rawCompliance.overall_risk || 0,
        });
        setPredictiveRisk({
            current: rawPredictive.current,
            day45: rawPredictive.day45,
            day90: rawPredictive.day90,
            day180: rawPredictive.day180,
            escalating180: rawPredictive.escalating180,
        });
    }, [rawCompliance, rawPredictive]);



    if (loading) return <LoadingSpinner />;

    return (
        <div className="min-h-screen bg-theme-primary">
            <Navigation />

            <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
                <div className="mb-3">
                    <h1 className="text-3xl font-bold text-content-primary">
                        <span className="text-purple-800">AI Dashboard </span>
                    </h1>
                    <p className="mt-2 text-gray-600">
                        <span className="text-purple-800">Comprehensive organizational risk overview</span>
                    </p>
                </div>

                {/* Intelligence Narrative — RISK_MANAGER+ at ORG only */}
                {canManageOrganizationRisks() && (
                    <IntelligenceNarrative
                        type="ai"
                        title="Intelligence Narrative"
                        subtitle="AI-generated briefing based on AI risks"
                        accentColor="amber"
                    />
                )}

                <div>
                    {loading ? (
                        <LoadingSpinner />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                            <OverallRiskScoreCard analytics={organizationAnalytics} />
                            <ComplianceStatusCard analytics={complianceData} organizationAnalytics={organizationAnalytics} loading={loadingCompliance} />
                            <VulnerabilityDistributionCard analytics={organizationAnalytics} />
                            <PredictiveRiskBars
                                data={predictiveRisk}
                                loading={loadingPredictive}
                                analytics={organizationAnalytics}
                            />
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-1 gap-2 mb-4" >
                    <RiskCategoryBarChart riskData={riskCategories} />
                </div>

                {/* Top 5 Recent Acquisitions - Tile Format - Moved above Remediation Tracker */}
                <div className="mb-4">
                    <RecentDealsCard />
                </div>

                {/* Remediation Status Tracker - Below Recent Acquisitions */}
                <div className="mb-4">
                    <RemediationStatusTracker />
                </div>

                {/* Recent Activities - hidden until dynamic data is implemented */}
                {/* <div className="mb-4">
                    <RecentActivityCard analytics={analytics} />
                </div> */}
            </div>
        </div>
    );
};

// Overall Risk Score Card with Speedo Meter Chart
const OverallRiskScoreCard = ({ analytics }) => {
    const { overall_risk_score = 0, overall_RiskLevel = "LOW", active_risks = 0, total_risks = 0 } = analytics || {};
    const hasAssessment = total_risks > 0;

    // Map risk levels to color stops
    const colors = ["#16a34a", "#ca8a04", "#b91c1c"]; // LOW → HIGH → CRITICAL

    return (
        <Card className="text-center p-4">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
                AI Risk Score
            </h3>

            <div className="w-full flex justify-center mb-4">
                <ReactSpeedometer
                    maxValue={100}
                    value={hasAssessment ? overall_risk_score : 0}
                    needleColor="#1f2937"
                    customSegmentStops={RISK_GAUGE_SEGMENT_STOPS}
                    segmentColors={RISK_GAUGE_SEGMENT_COLORS}
                    segments={RISK_GAUGE_SEGMENT_COLORS.length}
                    width={250}
                    height={150}
                    ringWidth={25}
                    needleTransitionDuration={1000}
                    needleTransition="easeElastic"
                    currentValueText={hasAssessment ? `${overall_risk_score}%` : " "}
                    needleHeightRatio={0.5}
                />
            </div>

            {hasAssessment ? (
                <>
                    <div className="flex justify-center space-x-2 mb-2">
                        {/*  <div className="text-2xl font-bold text-content-primary">{overall_risk_score}%</div>*/}
                        <div className={`text-xs px-2 py-1 rounded ${getRiskColor(overall_RiskLevel)}`}>
                            {overall_RiskLevel}
                        </div>
                    </div>
                    <div className="text-xs text-gray-500">{active_risks} Active Risks</div>
                    <RiskScoreFootnote />
                </>
            ) : (
                <div className="text-xs text-gray-400 italic">Assessment Not Started</div>
            )}
        </Card>
    );
};

const getRiskColor = (level) => {
    switch (level) {
        case "CRITICAL": return "text-red-700 bg-red-100";
        case "HIGH": return "text-orange-600 bg-orange-100";
        case "MEDIUM": return "text-yellow-600 bg-yellow-100";
        case "LOW": return "text-green-600 bg-green-100";
        default: return "text-gray-600 bg-gray-100";
    }
};





// Recent Acquisitions Card - Top 5 acquisitions in tile format for easy navigation
const RecentDealsCard = () => {
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingOrganizations, setLoadingOrganizations] = useState(true);
    const [businessUnits, setBusinessUnits] = useState([]);
    const [loadingBUs, setLoadingBUs] = useState(true);

    const apiCall = useApi();
    const navigate = useNavigate();
    const { canViewOrganizationAssessments } = useRBAC();

    // ----------------- Calculate Risk -----------------
    const calculateRiskScore = (riskCounts = {}, allTotal = 0) => {
        return severityPostureScore(riskCounts);
    };

    const getRiskLevelFromScore = (score) => severityPostureLevel(score);

    const getRiskColor = (riskLevel) => {
        switch (riskLevel) {
            case "CRITICAL":
                return "bg-red-700";
            case "HIGH":
                return "bg-orange-500";
            case "MEDIUM":
                return "bg-yellow-500";
            case "LOW":
                return "bg-green-500";
            default:
                return "bg-gray-400";
        }
    };

    // ----------------- Fetch Organizations -----------------
    const fetchOrganizations = useCallback(async (silent = false) => {
        try {
            const data = await apiCall("GET", `/AIOrganization/details`);
            const normalizedData = (Array.isArray(data) ? data : []).map((org) => ({
                ...org,
                hasAssessment: false,
                latestAssessment: null,
                // org-level, tag-aware AI total from the backend (matches the unified View Risks list)
                totalRisks: org.totalRisks ?? 0,
                // active (non-completed) count for the View Risks label; total keeps gating the button
                activeRisks: org.activeRisks,
                riskScore: 0,
                riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
            }));
            setOrganizations(normalizedData);

            if (normalizedData.length > 0) {
                setSelectedOrg(normalizedData[0]);
                fetchDeals(normalizedData[0].organizationId, silent);

                // fetch risk for each organization
                normalizedData.forEach((org) => fetchOrganizationRisk(org.organizationId));
            }
        } catch (err) {} finally {
            if (!silent) setLoadingOrganizations(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    // ----------------- Fetch Organization Risk -----------------
    const fetchOrganizationRisk = async (orgID) => {
        try {
            const org = await apiCall("GET", `/AIOrganization/${orgID}`);
            const latestAssessment = org.latestAssessment || null;

            if (latestAssessment) {
                // /AIOrganization/{id} now returns aggregate severity counts server-side
                // (riskCounts, active-only, camelCase keys) rather than the full risk array — see PR 598.
                const rc = latestAssessment.riskCounts || {};
                const riskCounts = {
                    CRITICAL: rc.critical || 0,
                    HIGH: rc.high || 0,
                    MEDIUM: rc.medium || 0,
                    LOW: rc.low || 0,
                };

                const riskScore = severityPostureScore(riskCounts);

                setOrganizations((prev) =>
                    prev.map((o) =>
                        o.organizationId === orgID
                            ? { ...o, latestAssessment, riskCounts, riskScore, hasAssessment: true }
                            : o
                    )
                );
            }
        } catch (err) {}
    };


    // ----------------- Fetch Deals -----------------
    const fetchDeals = async (orgId, silent = false) => {
        try {
            // Fetch deal metadata and risk summary in parallel
            const [allDealsData, summaryData] = await Promise.all([
                apiCall("GET", "/deals/all?category=AI"),
                apiCall("GET", "/deals/summary?category=AI&pageSize=100").catch(() => null),
            ]);

            const allDeals = Array.isArray(allDealsData) ? allDealsData : [];
            const top5 = allDeals
                .sort((a, b) => new Date(b.dealStartDate) - new Date(a.dealStartDate))
                .slice(0, 5);

            // Build summary lookup by dealId for risk data
            const summaryItems = summaryData?.items || [];
            const summaryByDealId = {};
            summaryItems.forEach((s) => { summaryByDealId[s.dealId] = s; });

            const top5WithRisk = top5.map((deal) => {
                const summary = summaryByDealId[deal.dealID];
                const riskCounts = {
                    CRITICAL: summary?.riskCounts?.critical || 0,
                    HIGH: summary?.riskCounts?.high || 0,
                    MEDIUM: summary?.riskCounts?.medium || 0,
                    LOW: summary?.riskCounts?.low || 0,
                };
                const totalRisks = Object.values(riskCounts).reduce((a, b) => a + b, 0);
                const riskScore = calculateRiskScore(riskCounts, totalRisks);
                return {
                    ...deal,
                    riskCounts,
                    riskScore,
                    hasAssessment: (summary?.applicationCount || 0) > 0,
                };
            });

            setDeals(top5WithRisk.sort((a, b) => {
                const aHasRisks = a.hasAssessment && Object.values(a.riskCounts).reduce((s, v) => s + v, 0) > 0;
                const bHasRisks = b.hasAssessment && Object.values(b.riskCounts).reduce((s, v) => s + v, 0) > 0;
                if (aHasRisks !== bHasRisks) return aHasRisks ? -1 : 1;
                return new Date(b.dealStartDate) - new Date(a.dealStartDate);
            }));
        } catch (err) {} finally {
            if (!silent) setLoading(false);
        }
    };

    // ----------------- Fetch Business Units -----------------
    const calcBURiskScore = (riskLevel) => {
        switch ((riskLevel || '').toUpperCase()) {
            case 'CRITICAL': return 95;
            case 'HIGH': return 80;
            case 'MEDIUM': return 60;
            case 'LOW': return 30;
            default: return 0;
        }
    };

    const fetchBusinessUnits = useCallback(async (silent = false) => {
        try {
            const data = await apiCall("GET", "/businessUnits/summary?category=AI&pageSize=5");
            const items = (data.items || []).map((bu) => ({
                ...bu,
                riskCounts: {
                    CRITICAL: bu.riskCounts?.critical || 0,
                    HIGH: bu.riskCounts?.high || 0,
                    MEDIUM: bu.riskCounts?.medium || 0,
                    LOW: bu.riskCounts?.low || 0,
                },
                totalRisks: (bu.riskCounts?.critical || 0) + (bu.riskCounts?.high || 0) +
                            (bu.riskCounts?.medium || 0) + (bu.riskCounts?.low || 0),
                riskScore: calcBURiskScore(bu.riskLevel),
                hasAssessment: (bu.applicationCount || 0) > 0,
            }));
            setBusinessUnits(items);
        } catch (err) {} finally {
            if (!silent) setLoadingBUs(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    // Consolidated refresh for the two independent mount fetches. `silent` skips loading toggles
    // so the live background refresh swaps data in place with no spinner.
    const refreshData = useCallback(async (silent = false) => {
        await Promise.all([
            fetchOrganizations(silent),
            fetchBusinessUnits(silent),
        ]);
    }, [fetchOrganizations, fetchBusinessUnits]);

    useEffect(() => { refreshData(false); }, [refreshData]);
    const liveRefreshDeals = useCallback(() => refreshData(true), [refreshData]);
    useLiveDashboard({ refresh: liveRefreshDeals });

    // ----------------- Render -----------------
    const orgsWithAssessment = organizations.filter(org => org.hasAssessment);
    return (
        <div className="space-y-4">
            {/* ------------------ ORGANIZATIONS ------------------ */}

            {canViewOrganizationAssessments() && (
                <Card>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Organization Assessment</h3>
                        <button
                            onClick={() => navigate("/AIOrganizations")}
                            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                            View →
                        </button>
                    </div>

                    {/* Show spinner while loading organizations */}
                    {loadingOrganizations ? (
                        <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        </div>
                    ) : orgsWithAssessment.length === 0 ? (
                        <div className="text-center py-2 text-gray-500"></div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {orgsWithAssessment.map((org) => {
                                const score = org.riskScore || 0;
                                const level = getRiskLevelFromScore(score);

                                return (
                                    <div
                                        key={org.organizationId}
                                        className={`p-4 border rounded-lg cursor-pointer transition ${selectedOrg?.organizationId === org.organizationId
                                            ? "border-purple-500 shadow"
                                            : "border-gray-300"
                                            }`}
                                        onClick={() => setSelectedOrg(org)}
                                    >
                                        <div className="flex justify-between items-center mb-2">
                                            <h4 className="font-semibold text-content-primary truncate">
                                                {org.organizationName}
                                            </h4>

                                            {(org.latestAssessment?.aiAssessmentID && org.latestAssessment?.aiStatus === "completed") ? (
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    className="px-2 py-1 h-8 text-xs flex items-center"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/AIAssessment/${org.latestAssessment.aiAssessmentID}/risks`);
                                                    }}
                                                >
                                                    <Shield className="h-4 w-4 mr-1" />
                                                    View Risks ({org.activeRisks ?? org.totalRisks})
                                                </Button>
                                            ) : (org.totalRisks ?? 0) > 0 ? (
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    className="px-2 py-1 h-8 text-xs flex items-center"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // No org assessment, but org-level (tagged) AI risks exist
                                                        navigate(`/risks?types=AI&scope=org`);
                                                    }}
                                                >
                                                    <Shield className="h-4 w-4 mr-1" />
                                                    View Risks ({org.activeRisks ?? org.totalRisks})
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    className="px-2 py-1 h-8 text-xs flex items-center"
                                                    disabled
                                                >
                                                    <Shield className="h-4 w-4 mr-1" />
                                                    No Risks
                                                </Button>
                                            )}
                                        </div>

                                        <p className="text-xs text-gray-500">Industry: {org.industry}</p>
                                        <p className="text-xs text-gray-500">Region: {org.regionName}</p>

                                        <div className="mt-2">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-xs text-gray-600">Risk Level</span>
                                                <span className="text-xs font-medium">{level}</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                <div
                                                    className={`h-1.5 rounded-full ${getRiskColor(level)}`}
                                                    style={{ width: `${score}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            )}

            {/* ------------------ DEALS ------------------ */}
            {/* ------------------ BUSINESS UNITS ------------------ */}
            {(loadingBUs || businessUnits.length > 0) && (
                <Card>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Business Units</h3>
                        <button
                            onClick={() => navigate("/AIBusinessUnits")}
                            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                            View All →
                        </button>
                    </div>
                    {loadingBUs ? (
                        <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            {businessUnits.map((bu) => {
                                const score = bu.riskScore || 0;
                                const level = getRiskLevelFromScore(score);
                                const hasRisk = bu.hasAssessment && bu.totalRisks > 0;
                                const badgeClass = level === 'CRITICAL' ? 'bg-red-100 text-red-700' : level === 'HIGH' ? 'bg-orange-100 text-orange-600' : level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600';
                                const scoreTextClass = level === 'CRITICAL' ? 'text-red-700' : level === 'HIGH' ? 'text-orange-600' : level === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600';
                                return (
                                    <div
                                        key={bu.businessUnitId}
                                        className="relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300"
                                        onClick={() => navigate("/AIBusinessUnits")}
                                    >
                                        <div className={`h-1 ${hasRisk ? getRiskColor(level) : 'bg-gray-200'}`} />
                                        <div className="p-4">
                                            <div className="flex justify-between items-start gap-2 mb-1.5">
                                                <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{bu.businessUnitName}</h4>
                                                {hasRisk && (
                                                    <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{level}</span>
                                                )}
                                            </div>
                                            {bu.domain && <p className="text-xs text-gray-400 mb-2">🌐 {bu.domain}</p>}
                                            {bu.assignedToUserName && (
                                                <div className="flex items-center gap-1.5 mb-2">
                                                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                                        <span className="text-[10px] font-bold text-gray-500">{bu.assignedToUserName[0]?.toUpperCase()}</span>
                                                    </div>
                                                    <span className="text-xs text-gray-500 truncate">{bu.assignedToUserName}</span>
                                                </div>
                                            )}
                                            {hasRisk ? (
                                                <div className="mb-2">
                                                    <div className="flex justify-between text-xs mb-1">
                                                        <span className="text-gray-400">Risk Score</span>
                                                        <span className={`font-bold ${scoreTextClass}`}>{score}%</span>
                                                    </div>
                                                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                        <div className={`h-full rounded-full ${getRiskColor(level)}`} style={{ width: `${score}%` }} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-xs text-gray-300 italic mb-2">No assessment yet</div>
                                            )}
                                            {hasRisk && (
                                                <div className="flex justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                                                    {bu.riskCounts.CRITICAL > 0 && <span className="text-red-600 font-medium">{bu.riskCounts.CRITICAL} Critical</span>}
                                                    {bu.riskCounts.HIGH > 0 && <span className="text-orange-500 font-medium">{bu.riskCounts.HIGH} High</span>}
                                                    {bu.riskCounts.MEDIUM > 0 && <span className="text-yellow-600 font-medium">{bu.riskCounts.MEDIUM} Med</span>}
                                                    {bu.riskCounts.LOW > 0 && <span className="text-green-600 font-medium">{bu.riskCounts.LOW} Low</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            )}

            {/* ------------------ DEALS ------------------ */}
            {(loading || deals.length > 0) && <Card>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">
                        Recent Deals
                    </h3>
                    <button
                        onClick={() => navigate("/AIDeals")}
                        className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                    >
                        View All →
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                        {deals.map((deal) => {
                            const score = deal.riskScore || 0;
                            const level = getRiskLevelFromScore(score);

                            const hasRisk = deal.hasAssessment && Object.values(deal.riskCounts || {}).reduce((a, b) => a + b, 0) > 0;
                            const badgeClass = level === 'CRITICAL' ? 'bg-red-100 text-red-700' : level === 'HIGH' ? 'bg-orange-100 text-orange-600' : level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600';
                            const scoreTextClass = level === 'CRITICAL' ? 'text-red-700' : level === 'HIGH' ? 'text-orange-600' : level === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600';
                            return (
                                <div
                                    key={deal.dealID}
                                    className="relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300"
                                    onClick={() => navigate("/AIDeals")}
                                >
                                    <div className={`h-1 ${hasRisk ? getRiskColor(level) : 'bg-gray-200'}`} />
                                    <div className="p-4">
                                        <div className="flex justify-between items-start gap-2 mb-1.5">
                                            <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{deal.dealName}</h4>
                                            {hasRisk && (
                                                <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{level}</span>
                                            )}
                                        </div>
                                        {(() => { const city = deal.dealCity || deal.DealCity; const loc = [city, deal.dealCountry].filter(Boolean).join(', '); return loc ? <p className="text-xs text-gray-400 mb-3">📍 {loc}</p> : null; })()}
                                        <div className="flex items-center gap-1.5 mb-3">
                                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                                <span className="text-[10px] font-bold text-gray-500">{(deal.dealOwner || 'N')[0]?.toUpperCase()}</span>
                                            </div>
                                            <span className="text-xs text-gray-500 truncate">{deal.dealOwner || 'N/A'}</span>
                                        </div>
                                        {hasRisk ? (
                                            <div className="mb-3">
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-gray-400">Risk Score</span>
                                                    <span className={`font-bold ${scoreTextClass}`}>{score}%</span>
                                                </div>
                                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div className={`h-full rounded-full ${getRiskColor(level)}`} style={{ width: `${score}%` }} />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-xs text-gray-300 italic mb-3">No assessment yet</div>
                                        )}
                                        {(() => { const fmt = (d) => { const dt = d ? new Date(d) : null; return dt && !isNaN(dt) && dt.getFullYear() > 2000 ? dt.toLocaleDateString() : null; }; const s = fmt(deal.dealStartDate); const e = fmt(deal.dealEndDate); return (s || e) ? <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100"><span>{s || '—'}</span><span className="text-gray-300">→</span><span>{e || '—'}</span></div> : null; })()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>}
        </div>
    );
};


// Compliance-percentage colors come from ../utils/riskColors (shared source of truth).

const ComplianceStatusCard = ({ analytics, organizationAnalytics, loading }) => {
    const complianceStatus = analytics?.compliance_status || {};
    // Backend now computes each category's bar (and this overall figure) with the same severity-weighted-count
    // formula the AI Risk Score gauge uses (AllRisksService.GetOrgRiskStatusWeightedUnifiedAsync), instead of
    // an average-severity-score per category — so this is guaranteed to equal the gauge, by construction,
    // rather than two independently-computed metrics that happened to share a label.
    const avgCompliance = analytics?.overall_compliance || 0;
    const [showAll, setShowAll] = useState(false);
    const LIMIT = 5;
    const hasAssessment = (organizationAnalytics?.total_risks || 0) > 0;

    const entries = Object.entries(complianceStatus).sort(([, a], [, b]) => (b ?? 0) - (a ?? 0));
    const visibleEntries = showAll ? entries : entries.slice(0, LIMIT);

    if (loading) {
        return (
            <Card className="text-center">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Risk Vector</h3>
                <div className="text-sm text-gray-500 py-6">Loading...</div>
            </Card>
        );
    }

    return (
        <Card className="text-center">
            <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Risk Vector </h3>
            </div>

            {hasAssessment && entries.length > 0 ? (
                <>
                    <div className="space-y-2">
                        {visibleEntries.map(([framework, percentage]) => (
                            <div key={framework} className="text-xs">
                                <div className="flex justify-between mb-1">
                                    <span className="text-gray-600">{framework}</span>
                                    <span className={`font-medium ${getComplianceColor(percentage).split(" ")[0]}`}>
                                        {percentage}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div
                                        className={`${getComplianceBarColor(percentage)} h-1.5 rounded-full`}
                                        style={{ width: `${percentage}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {entries.length > LIMIT && (
                        <button
                            onClick={() => setShowAll(v => !v)}
                            className="mt-3 w-full text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1 py-1 rounded hover:bg-indigo-50 transition-colors"
                        >
                            {showAll ? <>&#9650; Show less</> : <>&#9660; +{entries.length - LIMIT} more</>}
                        </button>
                    )}
                </>
            ) : (
                <>
                    <div className="space-y-2">
                        {['Category 1', 'Category 2', 'Category 3'].map((label) => (
                            <div key={label} className="text-xs">
                                <div className="flex justify-between mb-1">
                                    <span className="text-gray-300">{label}</span>
                                    <span className="text-gray-300">—</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-1.5" />
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 text-xs text-gray-400 italic">Assessment Not Started</div>
                </>
            )}
        </Card>
    );
};



// Vulnerability Distribution Card
const VulnerabilityDistributionCard = ({ analytics }) => {
    const { risk_distribution = {}, total_risks = 0, active_risks = 0, remediated_risks = 0 } = analytics || {};
    const hasAssessment = total_risks > 0;
    const criticalCount = risk_distribution.CRITICAL || 0;
    const highCount = risk_distribution.HIGH || 0;
    const mediumCount = risk_distribution.MEDIUM || 0;
    const lowCount = risk_distribution.LOW || 0;
    const infoCount = risk_distribution.INFORMATIONAL || 0;

    return (
        <Card className="text-center">
            <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Vulnerability Distribution</h3>
            </div>

            {hasAssessment ? (
                <>
                    {/* Show all risk levels with counts */}
                    <div className="space-y-2 mb-4">
                        {criticalCount > 0 && (
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-red-700 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">Critical</span>
                                </div>
                                <span className="text-sm font-bold text-red-700">{criticalCount}</span>
                            </div>
                        )}
                        {highCount > 0 && (
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">High</span>
                                </div>
                                <span className="text-sm font-bold text-orange-500">{highCount}</span>
                            </div>
                        )}
                        {mediumCount > 0 && (
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">Medium</span>
                                </div>
                                <span className="text-sm font-bold text-yellow-600">{mediumCount}</span>
                            </div>
                        )}
                        {lowCount > 0 && (
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">Low</span>
                                </div>
                                <span className="text-sm font-bold text-green-600">{lowCount}</span>
                            </div>
                        )}
                        {infoCount > 0 && (
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">Info</span>
                                </div>
                                <span className="text-sm font-bold text-blue-600">{infoCount}</span>
                            </div>
                        )}
                    </div>

                    {/* Risk counts summary */}
                    <div className="border-t pt-2 space-y-1">
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">Total Risks</span>
                            <span className="text-sm font-bold text-gray-800">{total_risks}</span>
                        </div>
                        {remediated_risks > 0 && (
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-green-600">Remediated</span>
                                <span className="text-sm font-bold text-green-600">{remediated_risks}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-blue-600">Active Risks</span>
                            <span className="text-sm font-bold text-blue-600">{active_risks}</span>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="space-y-2 mb-4">
                        {['Critical', 'High', 'Medium', 'Low'].map((label, i) => {
                            const colors = ['bg-red-200', 'bg-orange-200', 'bg-yellow-200', 'bg-green-200'];
                            return (
                                <div key={label} className="flex justify-between items-center">
                                    <div className="flex items-center">
                                        <div className={`w-3 h-3 ${colors[i]} rounded-full mr-2`}></div>
                                        <span className="text-sm font-medium text-gray-300">{label}</span>
                                    </div>
                                    <span className="text-sm font-bold text-gray-300">—</span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="border-t pt-2 space-y-1">
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">Total Risks</span>
                            <span className="text-sm font-bold text-gray-300">—</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">Active Risks</span>
                            <span className="text-sm font-bold text-gray-300">—</span>
                        </div>
                    </div>
                    <div className="mt-3 text-xs text-gray-400 italic">Assessment Not Started</div>
                </>
            )}
        </Card>
    );
};


// Smaller version of Risk Vector chart for dashboard cards
const ZeroTrustSpiderChartSmall = ({ zeroTrustScores, zeroTrustOverall, loading }) => {
    const categories = Object.entries(zeroTrustScores || {});
    const avgReadiness = zeroTrustOverall || (
        categories.length > 0
            ? Math.round(categories.reduce((sum, [_, value]) => sum + value, 0) / categories.length)
            : 0
    );

    const getMaturityColor = (score) => {
        if (score >= 80) return '#10B981'; // Green
        if (score >= 60) return '#F59E0B'; // Yellow
        if (score >= 40) return '#F97316'; // Orange
        return '#EF4444'; // Red
    };

    if (loading) {
        return (
            <Card>
                <h3 className="text-lg font-semibold mb-4">Zero Trust Readiness</h3>
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500 text-sm">Loading...</div>
                </div>
            </Card>
        );
    }

    if (!categories.length) {
        return (
            <Card>
                <h3 className="text-lg font-semibold mb-4">Zero Trust Readiness</h3>
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500 text-sm">No data</div>
                </div>
            </Card>
        );
    }

    const generateRadarPoints = (values) => {
        const centerX = 60;
        const centerY = 60;
        const maxRadius = 40;

        return values.map((value, index) => {
            const angle = (index * 2 * Math.PI) / values.length - Math.PI / 2;
            const radius = (value / 100) * maxRadius;
            const x = centerX + radius * Math.cos(angle);
            const y = centerY + radius * Math.sin(angle);
            return `${x},${y}`;
        }).join(' ');
    };

    const values = categories.map(([_, value]) => value);
    const radarPoints = generateRadarPoints(values);

    return (
        <Card>
            <h3 className="text-lg font-semibold mb-4">Zero Trust Readiness</h3>
            <div className="flex flex-col items-center">
                <div className="text-center mb-3">
                    <div className="text-2xl font-bold" style={{ color: getMaturityColor(avgReadiness) }}>
                        {avgReadiness}%
                    </div>
                    <div className="text-xs text-gray-600">Overall Readiness</div>
                </div>

                <svg width="120" height="120" className="mb-3">
                    {[25, 50, 75, 100].map(percent => (
                        <circle key={percent} cx="60" cy="60" r={(percent / 100) * 40} fill="none" stroke="#E5E7EB" strokeWidth="0.5" />
                    ))}

                    {categories.map((_, index) => {
                        const angle = (index * 2 * Math.PI) / categories.length - Math.PI / 2;
                        const x = 60 + 40 * Math.cos(angle);
                        const y = 60 + 40 * Math.sin(angle);
                        return <line key={index} x1="60" y1="60" x2={x} y2={y} stroke="#E5E7EB" strokeWidth="0.5" />;
                    })}

                    <polygon
                        points={radarPoints}
                        fill={getMaturityColor(avgReadiness)}
                        fillOpacity="0.3"
                        stroke={getMaturityColor(avgReadiness)}
                        strokeWidth="1.5"
                    />

                    {categories.map(([_, value], index) => {
                        const angle = (index * 2 * Math.PI) / categories.length - Math.PI / 2;
                        const radius = (value / 100) * 40;
                        const x = 60 + radius * Math.cos(angle);
                        const y = 60 + radius * Math.sin(angle);
                        return <circle key={index} cx={x} cy={y} r="2" fill={getMaturityColor(value)} />;
                    })}
                </svg>

                <div className="space-y-1 text-xs w-full">
                    {categories.map(([category, value]) => (
                        <div key={category} className="flex justify-between items-center">
                            <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 rounded" style={{ backgroundColor: getMaturityColor(value) }} />
                                <span className="text-gray-600 truncate">{category}</span>
                            </div>
                            <span className="font-medium">{value}%</span>
                        </div>
                    ))}
                </div>
            </div>
        </Card>
    );
};


// RiskCategoryBarChart for dashboard cards

const getCategoryBarColor = (score) => {
    if (score >= 75) return "#b91c1c";   // Red – Critical
    if (score >= 50) return "#f97316";   // Orange – High
    if (score >= 25) return "#eab308";   // Yellow – Medium
    if (score >= 10) return "#999999";
    return "#7f8080";                    // Green – Low
};

const RiskCategoryBarChart = ({ riskData }) => {
    const chartData = Object.entries(riskData || {}).map(([name, value]) => ({
        name,
        score: Number(value) || 0
    })).sort((a, b) => b.score - a.score);

    if (!chartData.length) {
        return (
            <Card>
                <h3 className="text-lg font-semibold mb-4">
                    Risk Exposure by Category
                </h3>
                <div className="text-gray-500 text-sm text-center py-10">
                    No risk category data available
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <h3 className="text-lg font-semibold mb-4">
                Risk Exposure by Category
            </h3>
            <div style={{ width: "100%", height: 250, maxHeight: 250 }}>
                <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                        data={chartData}
                        margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="name"
                            tickFormatter={(value) => value.length > 16 ? value.substring(0, 16) + '…' : value}
                            angle={-35}
                            textAnchor="end"
                            interval={0}
                            height={120}
                        />
                        <YAxis domain={[0, 100]}
                            ticks={[0, 20, 40, 60, 80, 100]}
                            interval={0}
                        />
                        <Tooltip />

                        <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={getCategoryBarColor(entry.score)}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};


// Main App Component
// RemediationStatusTracker Component for Dashboard
const RemediationStatusTracker = () => {
    const { user } = useAuth();
    const apiCall = useApi();  // Fix: useApi returns the function directly, not an object
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchRemediationSummary = useCallback(async (silent = false) => {
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) {
                return;
            }

            const response = await apiCall(
                'GET',
                `/aiRemediations/summary`
            );
            setSummary(response);
        } catch (error) {
            setSummary({
                totalRemediations: 0,
                byStatus: [],
                byPriority: [],
                overdueCount: 0,
                notStartedCount: 0,
                inProgressCount: 0,
                openCount: 0,
                completedCount: 0,
                top_in_progress: []
            });
        } finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    useEffect(() => { fetchRemediationSummary(false); }, [fetchRemediationSummary]);
    const liveRefresh = useCallback(() => fetchRemediationSummary(true), [fetchRemediationSummary]);
    useLiveDashboard({ refresh: liveRefresh });


    if (loading) return <LoadingSpinner />;

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'CRITICAL': return 'text-red-700 bg-red-100';
            case 'HIGH': return 'text-orange-600 bg-orange-100';
            case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
            case 'LOW': return 'text-green-600 bg-green-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'text-green-600 bg-green-100';
            case 'In Progress': return 'text-purple-600 bg-purple-100';
            case 'Waiting': return 'text-yellow-600 bg-yellow-100';
            case 'Under Review': return 'text-purple-600 bg-purple-100';
            case 'Deferred': return 'text-gray-600 bg-gray-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <Card>
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-content-primary flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-purple-600" />
                    Remediation Status Tracker
                </h3>
                <button
                    onClick={() => navigate('/remediations?types=AI')}
                    className="text-sm text-purple-600 hover:text-purple-800"
                >
                    View All →
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
                {/* 
                <div className="text-center">
                    <div className="text-2xl font-bold text-content-primary">{summary.totalRemediations}</div>
                    <div className="text-sm text-gray-500">Total Remediations </div>
                </div>
                */}
                <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{summary.notStartedCount}</div>
                    <div className="text-sm text-gray-500">Not Started</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{summary.inProgressCount}</div>
                    <div className="text-sm text-gray-500">In Progress</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{summary.completedCount}</div>
                    <div className="text-sm text-gray-500">Completed</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{summary.overdueCount}</div>
                    <div className="text-sm text-gray-500">Overdue</div>
                </div>
            </div>

            {summary.top_in_progress && summary.top_in_progress.length > 0 && (
                <div>
                    <h4 className="font-medium text-content-primary mb-3">Top In-Progress Tasks</h4>
                    <div className="space-y-2">
                        {summary.top_in_progress.slice(0, 5).map((remediation) => (
                            <div key={remediation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex-1">
                                    <div className="font-medium text-content-primary truncate">{remediation.title}</div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        📱 {remediation.applicationName || 'Not Linked'} | 🏢 {remediation.acquisition_name || 'Not Linked'}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate mt-1">
                                        ⚠️ Related to: {remediation.risk_name || 'Not Linked'}
                                    </div>
                                    <div className="flex items-center space-x-2 mt-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(remediation.priority)}`}>
                                            {remediation.priority}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(remediation.status)}`}>
                                            {remediation.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-medium text-content-primary">
                                        {remediation.progress_percentage || 0}%
                                    </div>
                                    <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
                                        <div
                                            className="bg-purple-600 h-2 rounded-full"
                                            style={{ width: `${remediation.progress_percentage || 0}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
};


// Remediation Update Modal Component
const RemediationUpdateModal = ({ remediation, onClose, onUpdate }) => {
    const [formData, setFormData] = useState({
        status: remediation.status || 'Not Started',
        progress_percentage: remediation.progress_percentage || 0,
        assigned_to: remediation.assigned_to || '',
        comments: ''
    });
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const { user } = useAuth();
    const apiCall = useApi();

    // Fetch users for assignment dropdown
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await apiCall('GET', '/users');
            setUsers(response || []);
        } catch (error) {
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onUpdate(remediation.id, formData);
    };

    const statusOptions = [
        'Not Started',
        'In Progress',
        'Waiting',
        'Under Review',
        'Completed',
    ];

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-content-primary">Update Remediation</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                                {statusOptions.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Progress Percentage: {formData.progress_percentage}%
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={formData.progress_percentage}
                                onChange={(e) => setFormData({ ...formData, progress_percentage: parseInt(e.target.value) })}
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Assigned To
                            </label>
                            <select
                                value={formData.assigned_to}
                                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                                disabled={loadingUsers}
                            >
                                <option value="">Unassigned</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.full_name || u.email}
                                    </option>
                                ))}
                            </select>
                            {loadingUsers && (
                                <p className="text-sm text-gray-500 mt-1">Loading users...</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Comment (Optional)
                            </label>
                            <textarea
                                value={formData.comments}
                                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                                placeholder="Add a comment about this update..."
                                rows="3"
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                            >
                                Update
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Recent Activity Card
const RecentActivityCard = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const apiCall = useApi();

    const timeAgo = (dateStr) => {
        const diff = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'Just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days}d ago`;
        return new Date(dateStr).toLocaleDateString();
    };

    const statusMeta = (status) => {
        const s = (status || '').toString();
        if (s === 'Completed') return { label: 'Completed', color: 'text-green-700 bg-green-100 border-green-200' };
        if (s === 'InProgress' || s === 'Analyzing') return { label: 'In Progress', color: 'text-yellow-700 bg-yellow-100 border-yellow-200' };
        return { label: 'Pending', color: 'text-blue-700 bg-blue-100 border-blue-200' };
    };

    const fetchActivities = useCallback(async (silent = false) => {
        try {
            const data = await apiCall('GET', '/aiAssessments');
            const sorted = (Array.isArray(data) ? data : [])
                .sort((a, b) => new Date(b.aiAssessmentDate) - new Date(a.aiAssessmentDate))
                .slice(0, 3);
            setActivities(sorted.map(a => ({
                title: a.dealName ? `Assessment — ${a.dealName}` : `AI Assessment #${a.aiAssessmentID}`,
                description: `${Math.round(a.aiProgress || 0)}% complete · ${a.aiAssessmentType || 'AI'}`,
                time: timeAgo(a.aiAssessmentDate),
                status: a.aiStatus,
                assignee: a.aiAssignedToUserName,
            })));
        } catch { setActivities([]); }
        finally { if (!silent) setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    useEffect(() => { fetchActivities(false); }, [fetchActivities]);
    const liveRefresh = useCallback(() => fetchActivities(true), [fetchActivities]);
    useLiveDashboard({ refresh: liveRefresh });

    if (loading) return (
        <Card><h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div></div>
        </Card>
    );

    if (activities.length === 0) return (
        <Card><h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <p className="text-sm text-gray-400 text-center py-4">No recent activity found</p>
        </Card>
    );

    return (
        <Card>
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {activities.map((activity, index) => {
                    const meta = statusMeta(activity.status);
                    return (
                        <div key={index} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${meta.color}`}>{meta.label}</span>
                                <span className="text-xs text-gray-400">{activity.time}</span>
                            </div>
                            <p className="text-sm font-semibold text-gray-800 truncate mb-1">{activity.title}</p>
                            <p className="text-xs text-gray-500">{activity.description}</p>
                            {activity.assignee && <p className="text-xs text-gray-400 mt-1">Assigned: {activity.assignee}</p>}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};


export default Dashboard;
